import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Trophy, CalendarClock } from "lucide-react";
import { KANBAN_STAGES, STAGE_DOTS, formatINR, fmtDate, fmtDateTime, avatarTone, initialsOf } from "@/lib/constants";
import { StatusBadge } from "./JobForm";

export const Overview = ({ stats, applications, deals }) => {
  const counts = Object.fromEntries(
    KANBAN_STAGES.map((s) => [s.id, applications.filter((a) => a.status === s.id).length])
  );
  const maxCount = Math.max(1, ...Object.values(counts));
  const openValue = deals.filter((d) => ["new", "negotiation"].includes(d.stage)).reduce((s, d) => s + (Number(d.value) || 0), 0);
  const wonValue = deals.filter((d) => d.stage === "won").reduce((s, d) => s + (Number(d.value) || 0), 0);
  const upcoming = applications
    .filter((a) => a.interview_at)
    .sort((a, b) => new Date(a.interview_at) - new Date(b.interview_at))
    .slice(0, 5);

  const statCards = stats
    ? [
        ["Open roles", stats.open_jobs],
        ["Applications", stats.applications],
        ["Enquiries", stats.enquiries],
        ["Employers", stats.employers],
        ["Candidates", stats.candidates],
        ["Total roles", stats.jobs],
      ]
    : [];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map(([label, value]) => (
          <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`stat-card-${label.replace(/\s+/g, "-").toLowerCase()}`}>
            <p className="font-display text-3xl font-semibold text-slate-900">{value}</p>
            <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-slate-400">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-7 rounded-2xl border border-slate-200 bg-white p-8" data-testid="pipeline-summary">
          <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Hiring pipeline</p>
          <div className="mt-6 space-y-3">
            {KANBAN_STAGES.map((s) => (
              <div key={s.id} className="flex items-center gap-4" data-testid={`pipeline-bar-${s.id}`}>
                <span className="w-24 shrink-0 flex items-center gap-2 text-xs text-slate-500">
                  <span className={`h-2 w-2 rounded-full ${STAGE_DOTS[s.id]}`} />
                  {s.label}
                </span>
                <div className="h-2 flex-1 rounded-full bg-slate-100 overflow-hidden">
                  <div className="h-full rounded-full bg-brand/80 transition-[width] duration-500" style={{ width: `${(counts[s.id] / maxCount) * 100}%` }} />
                </div>
                <span className="w-8 text-right text-xs text-slate-500">{counts[s.id]}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 rounded-2xl border border-slate-200 bg-white p-8" data-testid="deals-summary">
          <p className="text-xs uppercase tracking-[0.25em] text-slate-400">CRM pipeline</p>
          <div className="mt-6 space-y-6">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-3 text-sm text-slate-500"><TrendingUp size={16} className="text-brand" /> Open pipeline</span>
              <span className="font-display text-2xl font-semibold text-slate-900" data-testid="open-pipeline-value">{formatINR(openValue)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-3 text-sm text-slate-500"><Trophy size={16} className="text-emerald-500" /> Won business</span>
              <span className="font-display text-2xl font-semibold text-slate-900" data-testid="won-pipeline-value">{formatINR(wonValue)}</span>
            </div>
            <div className="flex items-center justify-between border-t border-slate-100 pt-5">
              <span className="text-sm text-slate-500">Active deals</span>
              <span className="font-display text-xl text-slate-900">{deals.filter((d) => ["new", "negotiation"].includes(d.stage)).length}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="upcoming-interviews">
        <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Who's interviewing, and when</p>
        {upcoming.length === 0 ? (
          <p className="mt-5 text-sm text-slate-400" data-testid="no-upcoming-interviews">
            No interviews scheduled — move a candidate to the Interview stage in the ATS board and pick a time.
          </p>
        ) : (
          <div className="mt-5 divide-y divide-slate-100">
            {upcoming.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 py-3.5" data-testid={`upcoming-interview-${a.id}`}>
                <div className="flex items-center gap-3">
                  <span className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold ${avatarTone(a.name)}`}>
                    {initialsOf(a.name)}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{a.name} <span className="font-normal text-slate-400">→ {a.job_title}</span></p>
                    <p className="text-xs text-slate-400">{a.job_company}</p>
                  </div>
                </div>
                <span className="flex items-center gap-2 rounded-full border border-violet-200 bg-violet-50 px-3.5 py-1.5 text-xs text-violet-700">
                  <CalendarClock size={13} /> {fmtDateTime(a.interview_at)} {a.interview_mode ? `· ${a.interview_mode}` : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-8" data-testid="recent-applications">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-[0.25em] text-slate-400">Latest applications</p>
          <Link to="/careers" data-testid="overview-view-jobs" className="group flex items-center gap-2 text-xs text-slate-500 hover:text-brand transition-colors duration-200">
            View job board <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
        {applications.length === 0 ? (
          <p className="mt-6 text-sm text-slate-400">No applications yet.</p>
        ) : (
          <div className="mt-5 divide-y divide-slate-100">
            {applications.slice(0, 5).map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 py-3.5" data-testid={`recent-app-${a.id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-900">{a.name} <span className="font-normal text-slate-400">→ {a.job_title}</span></p>
                  <p className="text-xs text-slate-400">{a.email} · {fmtDate(a.created_at)}</p>
                </div>
                <StatusBadge status={a.status} light />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
